import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import { Image, Link, Menu } from 'lego-on-react';
import * as React from 'react';

import { CheckboxDecorator } from '../../components/CheckboxDecorator/CheckboxDecorator';
import { Loader } from '../../components/Loader/Loader';
import { ModalDecorator } from '../../components/ModalDecorator/ModalDecorator';
import { ScopedPopup } from '../../components/ScopedPopup/ScopedPopup';
import { Shape2, Shape2Icon } from '../../components/Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../../components/StyledLink/StyledLink';
import { TextInputDecorator } from '../../components/TextInputDecorator/TextInputDecorator';
import { Bem } from '../../service/Bem/Bem';
import { copyTextToClipboard } from '../../service/Clipboard/Clipboard';
import { CanvasFooter } from '../CanvasFooter/CanvasFooter';
import { InfiniteList } from '../InfiniteList/InfiniteList';
import { VideoAdditionPreviewModal } from '../VideoAdditionPreviewModal/VideoAdditionPreviewModal';

import { CampaignInfo, VideoCreativeListItem, VideoCreativesProps } from './typings';
import './VideoCreatives.css';
import * as keyset from './VideoCreatives.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);
const BLOCK = 'video-creatives';

enum MenuItems {
    GET_ID = 'GET_ID',
    GET_CAMPAIGNS = 'GET_CAMPAIGNS',
    COPY_LINK = 'COPY_LINK'
}

enum KeyboardKeys {
    ENTER = 13,
    ESC = 27
}

interface CampaignsListProps {
    visible: boolean;
    loading: boolean;
    campaigns: CampaignInfo[] | undefined;
    close: () => void;
}

class CampaignsList extends React.PureComponent<CampaignsListProps> {
    render() {
        return (
            <ModalDecorator
                cls={Bem.cls(BLOCK, 'campaigns-popup')}
                theme={'clear'}
                visible={this.props.visible}
                progress={this.props.loading}
                hasClose={true}
                onClose={this.props.close}
                header={i18n('campaigns')}
            >
                {this.props.campaigns && this.props.campaigns.length > 0 ? (
                    <Menu
                        cls={Bem.cls(BLOCK, 'campaigns-list')}
                        theme="normal"
                        size="s"
                        type={'navigation'}
                        items={this.props.campaigns.map(cmp => ({
                            text: `${cmp.name} (${cmp.campaignId})`,
                            url: cmp.url,
                            target: 'blank'
                        }))}
                    />
                ) : (
                    <div className={Bem.cls(BLOCK, 'no-campaigns-warn')}>
                        {this.props.loading ? '' : i18n('campaigns-not-found')}
                    </div>
                )}
            </ModalDecorator>
        );
    }
}

interface NameEditorProps {
    item: VideoCreativeListItem;
    change: (id: string, val: string) => void;
    save: (id: string) => void;
    cancel: (id: string) => void;
}

class NameEditor extends React.PureComponent<NameEditorProps> {
    constructor(props: NameEditorProps) {
        super(props);
    }

    render() {
        return (
            <TextInputDecorator
                autoFocus={true}
                cls={Bem.cls(BLOCK, 'item-name-input')}
                focused={true}
                text={this.props.item.nameInputValue}
                size={'s'}
                theme={'normal'}
                view={'classic'}
                type={'text'}
                iconRight={<Shape2 width={16} height={16} icon={Shape2Icon.enter} />}
                onChange={this.onChange}
                onKeyDown={this.onKeyDown}
                onBlur={this.onBlur}
            />
        );
    }

    private onChange = (val: string) => {
        this.props.change(this.props.item.id, val);
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        const { keyCode } = event;
        const { id } = this.props.item;

        if ([KeyboardKeys.ENTER, KeyboardKeys.ESC].includes(keyCode)) {
            event.stopPropagation();
            if (keyCode === KeyboardKeys.ENTER) {
                this.props.save(id);
            } else {
                this.props.cancel(id);
            }
        }
    };

    private onBlur = () => {
        this.props.cancel(this.props.item.id);
    };
}

interface ItemControlsProps {
    item: VideoCreativeListItem;
    preview: (id: string) => void;
    enableEditMode: (id: string, enabled: boolean) => void;
    saveName: (id: string) => void;
    resetName: (id: string) => void;
    nameChange: (id: string, val: string) => void;
    getCampaigns: (id: string) => void;
    create: (id: string) => void;
}

class ItemControls extends React.PureComponent<ItemControlsProps> {
    render() {
        if (this.props.item.editMode) {
            return (
                <NameEditor
                    item={this.props.item}
                    change={this.props.nameChange}
                    save={this.props.saveName}
                    cancel={this.props.resetName}
                />
            );
        }

        return (
            <div
                className={Bem.cls(BLOCK, 'item-controls', {
                    hidden: this.props.item.editMode ? 'yes' : ''
                })}
            >
                <Link
                    theme={'ghost'}
                    pseudo={true}
                    onClick={this.onPreview}
                    cls={Bem.cls(BLOCK, 'item-controls-preview')}
                >
                    <Shape2
                        icon={Shape2Icon.expand}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'item-controls-icon')}
                    />
                    <span>{i18n('preview')}</span>
                </Link>
                <StyledLink
                    onClick={this.onCreate}
                    theme={StyledLinkTheme.grey}
                    cls={Bem.cls(BLOCK, 'item-controls-duplicate')}
                >
                    <Shape2
                        icon={Shape2Icon.duplicate}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'item-controls-icon')}
                    />
                    <span>{i18n('create')}</span>
                </StyledLink>
                <Link
                    title={i18n('rename-label')}
                    theme={'ghost'}
                    pseudo={true}
                    cls={Bem.cls(BLOCK, 'item-controls-edit')}
                    onClick={this.enableEditMode}
                >
                    <Shape2 icon={Shape2Icon.edit} width={12} height={12} cls={Bem.cls(BLOCK, 'item-controls-icon')} />
                </Link>
                <OtherActionsList item={this.props.item} getCampaigns={this.props.getCampaigns} />
            </div>
        );
    }

    private onCreate = () => {
        this.props.create(this.props.item.id);
    };

    private enableEditMode = () => {
        this.props.enableEditMode(this.props.item.id, true);
    };

    private onPreview = () => {
        this.props.preview(this.props.item.id);
    };
}

interface OtherActionsListProps {
    item: VideoCreativeListItem;
    getCampaigns: (id: string) => void;
}

interface OtherActionsListState {
    menuOpened: boolean;
}
class OtherActionsList extends React.PureComponent<OtherActionsListProps, OtherActionsListState> {
    private _link: Link | null;

    constructor(props: OtherActionsListProps) {
        super(props);
        this.state = {
            menuOpened: false
        };
    }

    render() {
        return (
            <>
                <Link
                    ref={link => (this._link = link)}
                    theme="pseudo"
                    cls={Bem.cls(BLOCK, 'item-controls-more')}
                    onClick={this.onLinkClick}
                >
                    <Shape2 width={16} height={4} icon={Shape2Icon.more} cls={Bem.cls(BLOCK, 'item-controls-icon')} />
                </Link>
                <ScopedPopup
                    theme={'normal'}
                    target={'anchor'}
                    anchor={this._link}
                    visible={this.state.menuOpened}
                    onOutsideClick={this.onOutsideClick}
                >
                    <Menu theme="normal" size="s" onClick={this.onMenuClick}>
                        <Menu.Item val={MenuItems.GET_ID}>
                            <span>ID: {this.props.item.creativeId}</span>
                        </Menu.Item>
                        <Menu.Item val={MenuItems.GET_CAMPAIGNS}>
                            <span>{i18n('campaigns-dropdown-label')}</span>
                        </Menu.Item>
                        <Menu.Item val={MenuItems.COPY_LINK}>
                            <span>{i18n('get-link-dropdown-label')}</span>
                        </Menu.Item>
                    </Menu>
                </ScopedPopup>
            </>
        );
    }

    private onLinkClick = () => {
        this.setState({
            menuOpened: !this.state.menuOpened
        });
    };

    private onOutsideClick = () => {
        this.setState({
            menuOpened: false
        });
    };

    private onMenuClick = (e: React.MouseEvent<HTMLElement>, val: string) => {
        if (val === MenuItems.COPY_LINK) {
            copyTextToClipboard(this.props.item.previewUrl);
        } else if (val === MenuItems.GET_CAMPAIGNS) {
            this.props.getCampaigns(this.props.item.id);
        } else if (val === MenuItems.GET_ID) {
            copyTextToClipboard(this.props.item.creativeId.toString());
        }
        this.setState({
            menuOpened: false
        });
    };
}

interface VideoCreativeRendererProps extends ItemControlsProps {
    toggleItemSelection: (id: string) => void;
    resetNameToInitial: (id: string) => void;
    saveName: (id: string) => void;
    resetName: (id: string) => void;
}

class VideoCreativeRenderer extends React.PureComponent<VideoCreativeRendererProps> {
    render() {
        return (
            <div className={Bem.cls(BLOCK, 'item')}>
                <CheckboxDecorator
                    checked={this.props.item.selected}
                    cls={Bem.cls(BLOCK, 'item-check')}
                    onChecked={this.toggleItemSelection}
                    item={this.props.item}
                >
                    <div className={Bem.cls(BLOCK, 'item-preview-wrapper')}>
                        <Image
                            cls={Bem.cls(BLOCK, 'item-preview', {
                                mode: this.props.item.shadowed ? 'select' : ''
                            })}
                            url={this.props.item.thumbnail}
                        />
                    </div>
                </CheckboxDecorator>
                <div className={Bem.cls(BLOCK, 'item-footer')}>
                    <div className={Bem.cls(BLOCK, 'item-name')}>
                        {this.props.item.hasPackshot && (
                            <Shape2
                                cls={Bem.cls(BLOCK, 'item-packshot')}
                                width={15}
                                height={12}
                                icon={Shape2Icon.packshot}
                            />
                        )}
                        {this.props.item.name}
                    </div>
                    <ItemControls
                        item={this.props.item}
                        preview={this.props.preview}
                        enableEditMode={this.props.enableEditMode}
                        saveName={this.props.saveName}
                        resetName={this.props.resetName}
                        nameChange={this.props.nameChange}
                        getCampaigns={this.props.getCampaigns}
                        create={this.props.create}
                    />
                </div>
            </div>
        );
    }

    private toggleItemSelection = (
        e: React.KeyboardEvent<HTMLElement>,
        data: { checked: boolean; item: VideoCreativeListItem }
    ) => {
        this.props.toggleItemSelection(data.item.id);
    };
}

export class VideoCreatives extends React.PureComponent<VideoCreativesProps> {
    componentDidMount(): void {
        this.onFilterTextChange(this.props.filterText);
    }

    render() {
        const { items } = this.props.collection;
        const submitEnabled = items.filter(item => item.selected).length > 0;

        return (
            <div className={Bem.cls(BLOCK)}>
                {this.getFilter()}
                <div className={Bem.cls(BLOCK, 'list')}>{this.getList()}</div>
                <CanvasFooter
                    cls={Bem.cls(BLOCK, 'footer')}
                    submitEnabled={submitEnabled}
                    submitVisible={true}
                    submitLabel={i18n('add-creatives')}
                    cancelLabel={i18n('cancel-and-return')}
                    error={this.props.error}
                    hideError={this.props.hideError}
                    submit={this.props.submit}
                    cancel={this.props.cancel}
                />
            </div>
        );
    }

    private getFilter() {
        return (
            <div className={Bem.cls(BLOCK, 'filters')}>
                <TextInputDecorator
                    size={'s'}
                    theme={'normal'}
                    cls={Bem.cls(BLOCK, 'input')}
                    placeholder={i18n('search-placeholder')}
                    text={this.props.filterText}
                    onChange={this.onFilterTextChange}
                />
                <CheckboxDecorator
                    cls={Bem.cls(BLOCK, 'show-checkbox')}
                    text={i18n('show-linked-only')}
                    size="s"
                    view="classic"
                    theme="normal"
                    tone="default"
                    checked={this.props.showTiedSelected}
                    onChecked={this.toggleTied}
                />
                <CheckboxDecorator
                    cls={Bem.cls(BLOCK, 'sound-checkbox')}
                    text={i18n('sound-setting')}
                    size="s"
                    view="classic"
                    theme="normal"
                    tone="default"
                    checked={this.props.playSoundSelected}
                    onChecked={this.togglePlaySound}
                />
            </div>
        );
    }

    private getList() {
        if (this.props.isBusy) {
            return <Loader size={'s'} />;
        }

        const { items } = this.props.collection;

        if (items.length > 0) {
            let videoPreviewData = undefined;
            let previewName = '';
            if (this.props.previewVisible) {
                videoPreviewData = {
                    vast: this.props.previewVast,
                    autostartoff: false,
                    soundOnStart: this.props.playSoundSelected
                };
                const item = items.find(cur => cur.id === this.props.previewId);
                previewName = item ? item.name : '';
            }

            return (
                <>
                    <VideoAdditionPreviewModal
                        creativeName={previewName}
                        isVisible={this.props.previewVisible}
                        onClose={this.props.closePreview}
                        onSave={this.selectItemFromPreview}
                        videoPreviewData={videoPreviewData}
                        isAudio={this.props.isAudio}
                    />
                    <CampaignsList
                        visible={this.props.campaignsListVisible}
                        loading={this.props.campaignsLoading}
                        campaigns={this.props.campaigns}
                        close={this.props.closeCampaignsList}
                    />
                    <InfiniteList content={this.getContent()} scrollAtBottom={this.onScrollAtBottom} />
                </>
            );
        } else {
            return (
                <div className={Bem.cls(BLOCK, 'empty-message')}>
                    {i18nRaw('items-not-found', {
                        presetsLink: (
                            <StyledLink key={'link'} theme={StyledLinkTheme.blue} onClick={this.onPresetCreate}>
                                {i18n('create-new')}
                            </StyledLink>
                        )
                    })}
                </div>
            );
        }
    }

    private getContent(): JSX.Element {
        const items: VideoCreativeListItem[] = this.props.collection.items;

        return (
            <>
                {items.map((item, index) => {
                    return (
                        <VideoCreativeRenderer
                            key={index}
                            item={item}
                            enableEditMode={this.enableEditMode}
                            saveName={this.onSaveName}
                            resetName={this.onResetName}
                            nameChange={this.props.nameChange}
                            getCampaigns={this.props.getCampaigns}
                            toggleItemSelection={this.props.toggleItemSelection}
                            preview={this.props.preview}
                            resetNameToInitial={this.props.resetNameToInitial}
                            create={this.props.openConstructor}
                        />
                    );
                })}
            </>
        );
    }

    private onPresetCreate = () => {
        this.props.openConstructor();
    };

    private onScrollAtBottom = () => {
        if (this.props.collection.offset < this.props.collection.total) {
            const { filterText, showTiedSelected, collection } = this.props;
            this.props.loadItems(collection.offset, filterText, showTiedSelected);
        }
    };

    private selectItemFromPreview = () => {
        this.props.setSelection(this.props.previewId);
        this.props.closePreview();
    };

    private onFilterTextChange = (val: string) => {
        this.props.filterChange(val, this.props.showTiedSelected);
    };

    private toggleTied = () => {
        this.props.filterChange(this.props.filterText, !this.props.showTiedSelected);
    };

    private togglePlaySound = () => {
        this.props.togglePlaySound();
    };

    private enableEditMode = (id: string, enabled: boolean) => {
        this.props.enableEditMode(id, enabled);
    };

    private onSaveName = (id: string) => {
        this.props.saveName(id);
        this.props.enableEditMode(id, false);
    };

    private onResetName = (id: string) => {
        this.props.resetNameToInitial(id);
        this.props.enableEditMode(id, false);
    };
}
